import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  Ref,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { ProgressBar } from './ProgressBar';

interface CarouselNavButtonProps {
  className?: string;
  ariaLabel?: string;
  dataCy?: string;
  onClick?: () => void;
}

export type CarouselNavButton<P extends CarouselNavButtonProps> =
  React.ComponentType<P>;

const CarouselDefaultButton = ({
  children,
  ariaLabel,
  dataCy,
  className,
  onClick,
}: React.PropsWithChildren<CarouselNavButtonProps>) => (
  <button
    className={clsx(
      'w-10 h-10 text-2xl leading-10 bg-neutral-100',
      'hover:bg-neutral-200 rounded-full',
      className,
    )}
    data-cy={dataCy}
    aria-label={ariaLabel}
    onClick={onClick}
  >
    {children}
  </button>
);

const CarouselDefaultPrevButton = ({
  className,
  onClick,
}: CarouselNavButtonProps) => (
  <CarouselDefaultButton
    className={className}
    ariaLabel="Previous Slide"
    dataCy="carousel-prev-button"
    onClick={onClick}
  >
    &lt;
  </CarouselDefaultButton>
);

const CarouselDefaultNextButton = ({
  className,
  onClick,
}: CarouselNavButtonProps) => (
  <CarouselDefaultButton
    className={className}
    ariaLabel="Next Slide"
    dataCy="carousel-next-button"
    onClick={onClick}
  >
    &gt;
  </CarouselDefaultButton>
);

interface CarouselNavButtonWrapperProps<P extends CarouselNavButtonProps> {
  onClick: () => void;
  ButtonComponent?: CarouselNavButton<P>;
  buttonComponentProps?: P;
  disabled?: boolean;
}

const CarouselPreviousWrapper = <P extends CarouselNavButtonProps>({
  onClick,
  ButtonComponent = CarouselDefaultPrevButton,
  buttonComponentProps,
  disabled = false,
}: CarouselNavButtonWrapperProps<P>) => (
  <ButtonComponent
    onClick={onClick}
    className={clsx(
      'z-10 absolute top-1/2 transform -translate-y-1/2 left-3',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    disabled={disabled}
    {...(buttonComponentProps as P)}
  />
);

const CarouselNextWrapper = <P extends CarouselNavButtonProps>({
  onClick,
  ButtonComponent = CarouselDefaultNextButton,
  buttonComponentProps,
  disabled = false,
}: CarouselNavButtonWrapperProps<P>) => (
  <ButtonComponent
    onClick={onClick}
    className={clsx(
      'z-10 absolute top-1/2 transform -translate-y-1/2 right-3',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    disabled={disabled}
    {...(buttonComponentProps as P)}
  />
);

interface CarouselProps<PrevProps extends object, NextProps extends object> {
  className?: string;
  toolsClassName?: string;
  ariaLabel?: string;
  slides: Array<React.ReactElement<CarouselSlideProps>>;
  initialSlideIdentifier?: string;
  showNav?: boolean;
  showProgressBar?: boolean;
  navPrevElement?: CarouselNavButton<PrevProps>;
  navPrevElementProps?: PrevProps & CarouselNavButtonProps;
  navNextElement?: CarouselNavButton<NextProps>;
  navNextElementProps?: NextProps & CarouselNavButtonProps;
  extraTools?: React.ReactNode;
  loop?: boolean;
  draggable?: boolean;
  /** When true, left/right arrow keys navigate the carousel. */
  enableArrowKeys?: boolean;
  onBeforeChange?: (slideIndex: number, slideIdentifier?: string) => boolean;
  onChange?: (
    slideIndex: number,
    lastSlideIndex?: number,
    slideIdentifier?: string,
    lastSlideIdentifier?: string,
  ) => void;
  onSettle?: (slideIndex: number, slideIdentifier?: string) => void;
}

export interface CarouselRef {
  scrollTo: (index: number) => void;
  scrollNext: () => void;
}

export const Carousel = forwardRef<CarouselRef, CarouselProps<any, any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  <PrevProps extends object = object, NextProps extends object = object>(
    {
      className,
      toolsClassName = 'pointer-events-none',
      ariaLabel = 'Carousel',
      slides,
      initialSlideIdentifier,
      showNav = true,
      showProgressBar = true,
      navPrevElement,
      navNextElementProps,
      navNextElement,
      navPrevElementProps,
      extraTools,
      loop = false,
      draggable = true,
      enableArrowKeys = false,
      onBeforeChange,
      onChange,
      onSettle,
    }: CarouselProps<PrevProps, NextProps>,
    ref: Ref<CarouselRef>,
  ) => {
    const [viewportRef, embla] = useEmblaCarousel({
      loop,
      watchDrag: draggable,
    });

    useImperativeHandle(ref, () => ({
      scrollTo: (index: number) => embla?.scrollTo(index),
      scrollNext: () => embla?.scrollNext(),
    }));

    const [initialised, setInitialised] = useState(false);

    const [currentSlideIndex, setCurrentSlideIndex] = useState<
      number | undefined
    >();

    const [progress, setProgress] = useState<number>(0);

    const handleBeforeSlideChange = useCallback(
      (slideIndex: number): boolean => {
        if (!embla) {
          return true;
        }
        if (!onBeforeChange) {
          return true;
        }

        const currentSlide = embla.slideNodes()[slideIndex];
        const identifier = currentSlide.getAttribute('data-identifier');

        return onBeforeChange(slideIndex, identifier || undefined);
      },
      [onBeforeChange, embla],
    );

    const handleSlideChange = useCallback(
      (newSlideIndex: number) => {
        if (!embla) {
          return;
        }

        if (!onChange) {
          return;
        }

        const currentSlide = embla.slideNodes()[newSlideIndex];
        const lastSlide =
          currentSlideIndex !== undefined
            ? embla.slideNodes()[currentSlideIndex]
            : undefined;

        onChange(
          newSlideIndex,
          currentSlideIndex,
          currentSlide.getAttribute('data-identifier') || undefined,
          lastSlide?.getAttribute('data-identifier') || undefined,
        );

        setCurrentSlideIndex(newSlideIndex);
      },
      [embla, onChange, currentSlideIndex],
    );

    const handleSlideSettle = useCallback(
      (newSlideIndex: number) => {
        if (!embla) {
          return;
        }

        if (!onSettle) {
          return;
        }

        const currentSlide = embla.slideNodes()[newSlideIndex];

        onSettle(
          newSlideIndex,
          currentSlide.getAttribute('data-identifier') || undefined,
        );

        setCurrentSlideIndex(newSlideIndex);
      },
      [embla, onSettle],
    );

    const initialise = useCallback(() => {
      if (!embla) {
        return;
      }
      if (initialised) {
        return;
      }

      handleSlideChange(0);
      handleSlideSettle(0);

      setInitialised(true);
    }, [embla, initialised, handleSlideChange]);

    const doSlideTo = useCallback(
      (slideIdentifier: string) => {
        if (!embla) {
          return;
        }
        if (!slideIdentifier) {
          return;
        }

        const slideIndex = embla
          .slideNodes()
          .findIndex(
            (slide) =>
              slide.getAttribute('data-identifier') === slideIdentifier,
          );

        if (slideIndex >= 0) {
          embla.scrollTo(slideIndex);
        }
      },
      [embla],
    );

    useEffect(() => {
      if (!embla) {
        return;
      }

      const handleInit = () => initialise();

      const handleSelect = () => {
        handleSlideChange(embla.selectedScrollSnap());

        setProgress(
          Math.round(
            (embla.selectedScrollSnap() / (embla.slideNodes().length - 1)) *
              100,
          ),
        );
      };

      const handleSettle = () => {
        handleSlideSettle(embla.selectedScrollSnap());
      };

      embla.on('init', handleInit);
      embla.on('reInit', handleInit);
      embla.on('select', handleSelect);
      embla.on('settle', handleSettle);

      return () => {
        embla.off('select', handleSelect);
        embla.off('settle', handleSettle);
      };
    }, [embla, initialise, handleSlideChange, handleSlideSettle]);

    useEffect(() => {
      if (!embla) {
        return;
      }

      if (initialSlideIdentifier) {
        doSlideTo(initialSlideIdentifier);
      }
    }, [embla, initialSlideIdentifier, doSlideTo]);

    useEffect(() => {
      if (!enableArrowKeys || !embla) {
        return;
      }

      const isEditableTarget = (el: EventTarget | null): boolean => {
        const node = el as HTMLElement | null;

        if (!node) {
          return false;
        }

        const tag = node.tagName;

        return (
          node.isContentEditable ||
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT'
        );
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (isEditableTarget(e.target)) {
          return;
        }

        if (e.key === 'ArrowRight') {
          // Respect onBeforeChange gating similar to next button
          if (handleBeforeSlideChange(embla.selectedScrollSnap())) {
            embla.scrollNext();
          }
        } else if (e.key === 'ArrowLeft') {
          embla.scrollPrev();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [enableArrowKeys, embla, handleBeforeSlideChange]);

    return (
      <>
        <div
          ref={viewportRef}
          className={clsx('relative overflow-hidden', className)}
          role="region"
          aria-label={ariaLabel}
        >
          <div className="flex h-full">{slides}</div>

          {showNav && (
            <div className={toolsClassName}>
              <CarouselPreviousWrapper
                onClick={() => embla?.scrollPrev()}
                ButtonComponent={navPrevElement}
                buttonComponentProps={navPrevElementProps}
                disabled={!embla?.canScrollPrev()}
              />

              {extraTools}

              <CarouselNextWrapper
                onClick={() => {
                  if (!embla) {
                    return;
                  }

                  if (handleBeforeSlideChange(embla?.selectedScrollSnap())) {
                    embla?.scrollNext();
                  }
                }}
                ButtonComponent={navNextElement}
                buttonComponentProps={navNextElementProps}
                disabled={!embla?.canScrollNext()}
              />
            </div>
          )}

          {showProgressBar && (
            <ProgressBar
              className="absolute top-0 left-0 right-0"
              progress={progress}
            />
          )}
        </div>
      </>
    );
  },
);

Carousel.displayName = 'Carousel';

export interface CarouselSlideProps extends React.PropsWithChildren {
  className?: string;
  classNameInner?: string;
  ariaLabel?: string;
  paddingX?: number;
  paddingY?: number;
  identifier?: string;
  useContainer?: boolean;
}

export type CarouselSlide = React.ReactElement<CarouselSlideProps>;

export const CarouselSlide = ({
  children,
  className,
  classNameInner,
  ariaLabel,
  paddingX,
  paddingY,
  identifier,
  useContainer = true,
}: CarouselSlideProps) => (
  <div
    className={clsx(
      'min-w-0 w-full h-full flex-grow-0',
      'flex-shrink-0 flex-basis-full',
      className,
    )}
    role="group"
    aria-roledescription="slide"
    aria-label={ariaLabel}
    data-identifier={identifier}
  >
    <div
      className={clsx(
        'mx-auto',
        useContainer && 'container',
        paddingX && `px-${paddingX}`,
        paddingY && `py-${paddingY}`,
        classNameInner,
      )}
    >
      {children}
    </div>
  </div>
);
